/* System defined core library */
import { Component, ElementRef, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Observer, Subscription, forkJoin } from 'rxjs';
import * as moment from 'moment';

/* User defined library/components */
import { UtilityService } from '../../../../shared/services/utility.service';
import { SchedulingApiService } from '../../services/scheduling-api.service';
import { SelectedExamDetailService } from '../../services/selected-exam-detail.service';
import { MapUtilityService } from '../../../../map/services/map-utility.service';
import { TimerService } from '../../../../shared/services/timer.service';

/* User defined models */
import { AppointmentDetail } from '../../../../shared/models/scheduling/appointmentdetail.model';
import { AppointmentSlot } from '../../../../shared/models/scheduling/appointmentSlot.model';
import { Client } from '../../../../shared/models/scheduling/client.model';
import { EligibilityDetail } from '../../../../shared/models/scheduling/eligibility.model';
import { Exam } from '../../../../shared/models/scheduling/exam.model';
import { Hold } from '../../../../shared/models/scheduling/hold.model';
import { Program } from '../../../../shared/models/scheduling/program.model';
import { Session } from '../../../../shared/models/scheduling/session.model';
import { SessionGroup } from '../../../../shared/models/scheduling/sessionGroup.model';
import { SiteSlot } from '../../../../shared/models/scheduling/siteSlot.model';
import { Slot } from '../../../../shared/models/scheduling/slot.model';
import { TestingAccommodation } from '../../../../shared/models/scheduling/testingAccommodation.model';

/* User defined enums/constants */
import * as constants from '../../../../shared/constants/scheduling.constant';
import { ComponentType } from '../../../../shared/enums/scheduling.enum';


@Component({
  selector: 'app-multiday-session-detail',
  templateUrl: './multiday-session-detail.component.html',
  styleUrls: ['./multiday-session-detail.component.scss']
})
export class MultidaySessionDetail implements OnInit {

  /* User defined private/public variables  */

  public client: Client;
  public program: Program;
  public exam: Exam;
  public popupDateSelection: any;
  public sites: SiteSlot[];
  public selectedAppointments: AppointmentDetail[] = []
  public applicableTestingAccommodations: TestingAccommodation[];
  public isMapsValid: boolean = true;
  public examWithTA: Exam[] = [];
  public eligibilityDetails: EligibilityDetail[] = [];
  public selectedSite: SiteSlot;
  public holds: Hold[];

  public sessions: Session[] = [];
  public maxSessionLength: number = 0;
  public sessionGroups: SessionGroup[] = [];
  public sessionAvailabilities: any[];
  public selectedSlots: Slot[] = [];
  public selectedSessions: Session[] = [];
  public noSessionAvailability: boolean;

  public zoom: number;
  public zoomDefault: number = 12;
  public zoomOnFocus: number = 15;
  public markers: any = [];
  public lat: number = constants.DefaultLatitude;
  public lng: number = constants.DefaultLognitude;
  public placeHolderText: string;

  public today = {
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    day: new Date().getDate()
  }
  public defaultCalendarMonth: string;
  public maxEndDateLimit: string;
  public overLapEndDate: any;
  public overLapStartDate: any;

  public myDateRangePickerOptions:any;

  /* Input-OutPut parameters decalartions */
  @Output() canNavigateNext: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() chosenSessions: EventEmitter<Session[]> = new EventEmitter<Session[]>();


  /* Service subscriptions */
  client$: Subscription;
  program$: Subscription;
  exam$: Subscription;
  eligibility$: Subscription;
  sites$: Subscription;
  defaultCalendarMonth$: Subscription;
  selectedStartDate$: Subscription;
  selectedEndDate$: Subscription;
  overLapStartDate$: Subscription;
  overLapEndDate$: Subscription;
  maxEndDateLimit$: Subscription;
  holds$: Subscription;
  selectedAppointments$: Subscription;
  skipSize$: Subscription;
  eligibilityDetails$: Subscription;
  testingAccommodation$: Subscription;
  translateLanguage$: Subscription;
  selectedSite$: Subscription;
  sessions$: Subscription;
  sessionGroups$: Subscription;
  sessionAvailabilities$: Subscription;

  @ViewChild('testcenterLocation') testcenterLocation: ElementRef;

  constructor(
    private mapUtilityService: MapUtilityService,
    private schedulingApiService: SchedulingApiService,
    private selectedExamDetailService: SelectedExamDetailService,
    private timerService: TimerService,
    private translate: TranslateService,
    private utilityService: UtilityService
  ) {
    translate.setDefaultLang(this.selectedExamDetailService.defaultLanguageCode);
    this.translateLanguage$ = selectedExamDetailService.languageCode.subscribe(
      languageCode => {
        translate.use(languageCode);
      });

    this.client$ = selectedExamDetailService.client.subscribe(
      client => {
        this.client = client;
      });

    this.program$ = selectedExamDetailService.program.subscribe(program => {
      this.program = program;
    });

    this.defaultCalendarMonth$ = selectedExamDetailService.defaultCalendarMonth.subscribe(defaultCalendarMonth => {
      this.defaultCalendarMonth = defaultCalendarMonth;
    });

    this.overLapStartDate$ = selectedExamDetailService.overlapsFromDate.subscribe(
      startDate => {
        this.overLapStartDate = startDate;
      });

    this.overLapEndDate$ = selectedExamDetailService.overlapsToDate.subscribe(
      endDate => {
        this.overLapEndDate = endDate;
      });

    this.maxEndDateLimit$ = selectedExamDetailService.maxEndDateLimit.subscribe(
      endDate => {
        this.maxEndDateLimit = endDate;
      });

    this.exam$ = selectedExamDetailService.exam.subscribe(exam => {
      this.exam = exam;
    });

    this.zoom = this.zoomDefault;

    this.selectedAppointments$ = selectedExamDetailService.selectedAppointmentDetails.subscribe(
      selectedAppointments => {
        this.selectedAppointments = selectedAppointments;
      });

    this.testingAccommodation$ = selectedExamDetailService.applicableTestingAccommodations.subscribe(
      testingAccommodation => {
        this.applicableTestingAccommodations = testingAccommodation;
      });

    this.eligibilityDetails$ = selectedExamDetailService.eligibilityDetails.subscribe(value => {
      this.eligibilityDetails = value || [];
    });

    this.sites$ = selectedExamDetailService.prevSiteSlots.subscribe(sites => {
      this.sites = sites;
    });

    this.sessions$ = selectedExamDetailService.sessions.subscribe(sessions => {
      if (sessions && sessions.length) {
        this.sessions = sessions;
      }
    });

    this.sessionGroups$ = selectedExamDetailService.sessionGroups.subscribe(sessionGroups => {
      if (sessionGroups && sessionGroups.length) {
        this.sessionGroups = sessionGroups;
      }
    });

    this.sessionAvailabilities$ = selectedExamDetailService.sessionAvailabilities.subscribe(sessionAvailabilities => {
      if (sessionAvailabilities && sessionAvailabilities.length) {
        this.sessionAvailabilities = sessionAvailabilities;
      }
    });

    this.selectedSite$ = selectedExamDetailService.siteSlot.subscribe(siteSlot => {
      this.selectedSite = siteSlot;
    });

    this.holds$ = selectedExamDetailService.holds.subscribe(holds => {
      this.holds = holds;
    });

    this.placeHolderText = this.selectedExamDetailService.dateFormat.toUpperCase();

    this.popupDateSelection = {
      title: "",
      content: "long page string .....................",
      placement: "bottom",
      actionTxt: "testCenterSelecterComponent.moreDatesAtThisTestCenter",
      appliedClass: "btn moreAction",
      showHeaderFooter: false,
      refBlock: "SessionDate",
      locationSelector: false,
      isSlotSelected: false,
      focusChildElement: 'calendarWrpr'
    };

    this.isMapsValid = this.selectedExamDetailService.isGoogleMapsValid;
  }


  ngOnInit() {
    if (!this.utilityService.isDataValidToLoadComponent(ComponentType.MultidayTestCenterSelectorComponent)) return;
    this.exam = this.selectedAppointments[0].exam;
    this.maxSessionLength = this.exam.sessions.length;

    this.validateNavigation();

    this.examWithTA = this.selectedAppointments.map((appointment) => {
      return Exam.convertAPIObjectToExam(appointment.exam, appointment.exam.sessions, appointment.status, appointment.exam.testingAccommodations);
    });

    if (this.selectedSite) {
      if (this.sessions && !this.sessions.length && !!this.selectedAppointments[0].exam.sessions && this.selectedAppointments[0].exam.sessions.length) {
        let examSessions = this.selectedAppointments[0].exam.sessions;
        this.selectedSessions = this.selectedAppointments[0].exam.sessions.filter(s => s.selected);
        let firstSessionSelectedDate = this.selectedAppointments[0].exam.sessions[0].selectedDate;
        this.loadMapWithMarkers([this.selectedSite]);
        this.sites = [this.selectedSite];

        this.clearSessions();
        this.setMaxEndDate(firstSessionSelectedDate);

        this.selectedSite.Testdate = firstSessionSelectedDate;
        this.selectedExamDetailService.setSiteSlot(this.selectedSite);
        this.selectedAppointments[0].appointmentSlot = new AppointmentSlot();
        this.selectedAppointments[0].hold = new Hold();

        this.getSessionGroups(firstSessionSelectedDate, this.maxEndDateLimit, this.selectedSite).subscribe(() => {
          examSessions.forEach((s, index) => {
            if (s.slot && s.slot.startDateTime && index > 0) {
              this.setSessionData(null, moment(s.slot.startDateTime).format('YYYY-MM-DDTHH:mm:ss'), index, 1, s);
            }
          });
        });
      }

      // Map changes
      this.lat = this.selectedSite.location.coordinates[0]; //focus in the site pin on map
      this.lng = this.selectedSite.location.coordinates[1];
      this.zoom = this.zoomOnFocus;
      this.loadMapWithMarkers([this.selectedSite]);
    }
  }

  getValidStart() {
    return this.utilityService.getValidStart(this.overLapStartDate);
  }

  getValidStartDateEnd() {
    return this.utilityService.getValidStartDateEnd(this.overLapStartDate, this.overLapEndDate);
  }

  /* Load markers based on available sites */
  loadMapWithMarkers(sites: SiteSlot[]): void {
    this.markers = this.mapUtilityService.getMapMarkersFromSlots(sites);
  }

  /**
   * UX changes on site selection
   * Hide other map markers and sites
   * @param event
   * @param selectedStartDate
   * @param site
   */
  selectSite(event: any, selectedStartDate: any, site: SiteSlot): void {
    $('.card-heading.card-heading-sm.test-center-monthdiv').removeClass('active');
    $('.card-heading card-heading-sm.test-center-month').removeClass('active');
    if (event && event.target) {
      $(event.target).addClass('active');
    }

    // set site
    site.Testdate = selectedStartDate;
    this.selectedSite = site;
    this.selectedExamDetailService.setSiteSlot(this.selectedSite);

    this.selectedAppointments[0].appointmentSlot = new AppointmentSlot();
    this.selectedAppointments[0].hold = new Hold();

    // Map changes
    this.lat = site.location.coordinates[0]; //focus in the site pin on map
    this.lng = site.location.coordinates[1];
    this.zoom = this.zoomOnFocus;
    this.loadMapWithMarkers([this.selectedSite]);

    // set session dates
    this.setMaxEndDate(selectedStartDate);
    this.getSessionGroups(this.selectedSite.Testdate, this.maxEndDateLimit).subscribe(() => {});
  }

  // set first session
  setFirstSession(site: SiteSlot, selectedDate:string, startDate: string, endDate: string, index: number) {
    if (index === 0) {
      const date = moment(selectedDate).format('YYYY-MM-DDTHH:mm:ss');
      const firstSession = new Session(
        this.sessions[0].startDate,
        this.sessions[0].endDate,
        this.sessions[0].site,
        this.getFirstSessionAvailability(),
        false,
        this.exam.sessions[0].duration,
        this.exam.sessions[0].confirmationNumber,
        date,
        1,
        1,
        this.utilityService.getTimeString(date, this.selectedExamDetailService.timeFormat),
        this.utilityService.getTimeString(this.getEndDateTime(date, this.exam.sessions[0].duration), this.selectedExamDetailService.timeFormat)
      );

      this.sessions[0] = firstSession;
      this.setMaxEndDate(selectedDate);

      this.exam.sessions[0].site = site;
      this.exam.sessions[0].slot = new Slot(null, null, new Date(selectedDate));
      this.exam.sessions[0].slot.testTime =
        this.utilityService.getTimeString(
          moment(this.exam.sessions[0].slot.startDateTime).format("YYYY-MM-DDTHH:mm:ss"), this.selectedExamDetailService.timeFormat
        );
      this.exam.sessions[0].slot.testEndTime =
        this.utilityService.getStartEndFormat(
          UtilityService.addMins(
            this.exam.sessions[0].slot.startDateTime,
            this.sessions[0].duration
          )
        );
      this.chosenSessions.emit(this.exam.sessions);

      this.setMaxEndDate(selectedDate);

      let sessionLength = this.sessions.length;
      let session = new Session(
        moment(this.sessions[sessionLength - 1].selectedDate).add(1, 'days').format('YYYY-MM-DD'),
        this.maxEndDateLimit,
        site,
        this.getSessionAvailabilities(),
        this.exam.sessions[1].selected,
        this.exam.sessions[1].duration,
        this.exam.sessions[1].confirmationNumber
      );
      session.hold = this.exam.sessions[1].hold;
      session.slot = this.exam.sessions[1].slot;
      session.defaultMonth = this.utilityService.getEligibleMonth(new Date(session.startDate));
      this.sessions.push(session);

      this.myDateRangePickerOptions = {
        // other options...
        dateFormat: this.selectedExamDetailService.dateFormat.toLowerCase(),
        sunHighlight: false,
        disableUntil: this.getValidStart(),
        disableSince: this.getValidStartDateEnd(),
        firstDayOfWeek: 'su',
        disableDates: [this.today],
        showTodayBtn: false,
        placeholderText: 'MM/DD/YYYY - MM/DD/YYYY'
      }
    }
  }

  // when a date is selected
  setSessionData($event: any, selectedDate: string, index: number, groupId: number, selectedSession?: Session) {
    this.clearHolds();
    if (index === 0) {
      this.setFirstSession(this.selectedSite, selectedDate, this.overLapStartDate, this.overLapEndDate, 0);
    }

    this.sessions[index].selectedDate = selectedDate;
    this.sessions[index].groupId = groupId;
    this.sessions[index].sessionId = index + 1;
    this.sessions[index].duration = this.exam.sessions[index].duration;
    this.sessions[index].startDateTime = this.utilityService.getTimeString(selectedDate, this.selectedExamDetailService.timeFormat);
    this.sessions[index].endDateTime = this.utilityService.getTimeString(this.getEndDateTime(selectedDate, this.exam.sessions[index].duration), this.selectedExamDetailService.timeFormat);
    this.sessions[index].site = this.selectedSite;
    if (selectedSession) {
      this.sessions[index].selected = selectedSession.selected;
      this.sessions[index].confirmationNumber = selectedSession.confirmationNumber;
      this.sessions[index].slot = selectedSession.slot;
      this.sessions[index].hold = selectedSession.hold;
    }

    this.exam.sessions[index].site = this.selectedSite;
    this.exam.sessions[index].slot = new Slot(null, null, new Date(selectedDate));
    this.exam.sessions[index].slot.testTime =
      this.utilityService.getTimeString(
        moment(this.exam.sessions[index].slot.startDateTime).format("YYYY-MM-DDTHH:mm:ss"), this.selectedExamDetailService.timeFormat
      );
    this.exam.sessions[index].slot.testEndTime =
      this.utilityService.getStartEndFormat(
        UtilityService.addMins(
          this.exam.sessions[index].slot.startDateTime,
          this.sessions[index].duration
        )
      );
    this.chosenSessions.emit(this.exam.sessions);

    let sessionLength = this.sessions.length;
    if (this.sessions.length > (index + 1)) {
      this.clearSelection(null, index + 1);
      this.sessions = this.sessions.slice(0, index + 1);
      let session = new Session();
      session.endDate = this.maxEndDateLimit;
      session.startDate = moment(selectedDate).add(1, 'days').format('YYYY-MM-DD');
      session.defaultMonth = this.utilityService.getEligibleMonth(new Date(session.startDate));
      session.availabilities = this.getSessionAvailabilities();
      session.site = this.selectedSite;
      session.selected = true;
      this.sessions.push(session);
    } else if (this.sessions.length < this.maxSessionLength) {
      let session = new Session();
      session.endDate = this.maxEndDateLimit;
      session.startDate = moment(this.sessions[sessionLength - 1].selectedDate).add(1, 'days').format('YYYY-MM-DD');
      session.availabilities = this.getSessionAvailabilities();
      session.defaultMonth = this.utilityService.getEligibleMonth(new Date(session.startDate));
      session.site = this.selectedSite;
      session.selected = true;
      this.sessions.push(session);
    }

    if (this.sessions.length === this.maxSessionLength) {
      if (this.sessions.every(s => !!s.selectedDate)) {
        this.setHolds();
      } else {
        this.selectedSlots = [];
      }
    }

    this.validateNavigation();
  }

  /**
   * When date is selected from RangePicker
   * @param selectedDate
   * @param rangeType
   * @param index
   */
  setSessionRangeDate(selectedDate: any, rangeType: string) {
    if (rangeType === 'start') {
      this.sessions[0].selectedRangeStartDate = selectedDate;
      const endDateBegin = this.getValidEndDateStart(selectedDate.date);
      const endDateEnd = this.getValidEndDateEnd(endDateBegin);
      this.myDateRangePickerOptions = {
        dateFormat: this.selectedExamDetailService.dateFormat.toLowerCase(),
        sunHighlight: false,
        disableUntil: endDateBegin,
        disableSince: endDateEnd,
        firstDayOfWeek: 'su',
        disableDates: [this.today],
        showTodayBtn: false
      };
    } else {
      // rangetype is end
      this.sessions[0].selectedRangeEndDate = selectedDate;
      this.selectedSite.Testdate = null;

      this.getSessionGroups(this.sessions[0].selectedRangeStartDate.jsdate, this.sessions[0].selectedRangeEndDate.jsdate).subscribe(() => {
        this.sessions[0].selectedDate = null;
        this.sessions[0].availabilities = this.getFirstSessionAvailability();
        this.clearSelection(null, 0);
      });

      this.myDateRangePickerOptions = {
        // other options...
        dateFormat: this.selectedExamDetailService.dateFormat.toLowerCase(),
        sunHighlight: false,
        disableUntil: this.getValidStart(),
        disableSince: this.getValidStartDateEnd(),
        firstDayOfWeek: 'su',
        disableDates: [this.today],
        showTodayBtn: false,
        placeholderText: 'MM/DD/YYYY - MM/DD/YYYY'
      }
    }
  }

  /**
   * Get first session availabilities
   * */
  getFirstSessionAvailability() {
    let result = this.sessionGroups.map((sg) => {
      return {
        "groupId": sg.groupId,
        "date": sg.sessions[0].slot.startDateTime,
        "slotId": sg.sessions[0].slot.id
      }
    });
    result = this.getDistinctValues(result);
    return result;
  }

  /**
   * get all session availabilities
   * */
  getSessionAvailabilities() {
    let groups: any = [];
    this.sessions.forEach((session, index) => {
      let selectedDate = moment(session.selectedDate).format('YYYY-MM-DD');
      let sessionGrp = this.sessionGroups.map((sg) => {
        let avail = sg.sessions.filter((s) => {
          return (s.sessionId === index + 1) && moment(s.slot.startDateTime).format('YYYY-MM-DD') === selectedDate;
        })
        if (avail.length) return avail[0].groupId;
      });
      sessionGrp = sessionGrp.filter(sg => !!sg);
      groups.push(sessionGrp);
    })
    groups = this.getCommonElements(groups);
    let result = this.sessionGroups.map((sg) => {
      if (groups.indexOf(sg.groupId) > -1) {
        return {
          "groupId": sg.groupId,
          "date": sg.sessions[this.sessions.length].slot.startDateTime,
          "slotId": sg.sessions[this.sessions.length].slot.id
        }
      }
    });
    result = result.filter(r => !!r);
    result = this.getDistinctValues(result);
    return result;
  }

  /**
   * Set availabilitires when a date from datepicker is chosen
   * @param $event
   * @param index
   */
  getSessionData($event: any, index: number) {
    const date = moment(new Date($event['jsdate'])).format('YYYY-MM-DD');

    const previousSessionAvail = this.sessionAvailabilities[index - 1];

    if (previousSessionAvail && previousSessionAvail.length) {
      const doesAvailMatch = previousSessionAvail.filter((sa: any) => {
        return moment(sa.startDateTime).format('YYYY-MM-DD') === moment(this.sessions[index - 1].selectedDate).format('YYYY-MM-DD');
      });

      if (doesAvailMatch.length) {
        let availabilities = this.sessionAvailabilities[index];
        if (availabilities) {
          const selected = availabilities.filter((a: any) => {
            return a.testDate === date;
          });
          if (selected.length) {
            this.noSessionAvailability = false;
            this.sessions[index].availabilities = [{
              "groupId": selected[0].groupId,
              "date": selected[0].slot.startDateTime,
              "slotId": selected[0].slot.id
            }]
            this.setSessionData(null, selected[0].startDateTime, index, selected[0].groupId);
          } else {
            this.noSessionAvailability = true;
          }
        }
      } else {
        this.noSessionAvailability = true;
      }
    } else {
      this.noSessionAvailability = true;
    }
  }


  setMaxEndDate(selectedStartDate: any) {
    const maxEndDate = moment(selectedStartDate).add(
      this.selectedExamDetailService.maxDays
      , 'day').format('YYYY-MM-DD');
    const overLap = moment(this.overLapEndDate, 'MM-DD-YYYY').format('YYYY-MM-DD');

    if (moment(overLap).isBefore(maxEndDate)) {
      this.maxEndDateLimit = overLap;
      this.selectedExamDetailService.setmaxEndDateLimit(overLap);
    } else {
      this.maxEndDateLimit = maxEndDate;
      this.selectedExamDetailService.setmaxEndDateLimit(maxEndDate);
    }
  }

  setHolds() {
    const selectedGroupId = this.sessions[this.sessions.length - 1].groupId;
    const selectedGroup = this.sessionGroups.filter((sg) => sg.groupId === selectedGroupId);
    if (selectedGroup.length) {
      selectedGroup.forEach((sg) => {
        this.selectedSlots = sg.sessions.map(s => s.slot);
      })
    }

    this.sessions.forEach((s, i) => {
      if (s.selected) {
        s.slot = this.selectedSlots[i];
        s.hold = new Hold();
        s.hold.slot = this.selectedSlots[i];
      }
    });
    this.selectedExamDetailService.setSessions(this.sessions);
    this.chosenSessions.emit(this.sessions);
  }

  validateNavigation() {
    if (!this.holds || (this.holds && !this.holds.length)) {
      this.canNavigateNext.emit(!!this.selectedSlots.length);
    } else {
      this.canNavigateNext.emit(!!(this.holds && this.holds.length));
    }
  }

  /**
   * Clear session selection based on Index
   * @param $event
   * @param index
   */
  clearSelection($event: any, index: number) {
    this.sessions.forEach((session, i) => {
      if (i >= index) {
        session.selectedDate = '';
        session.groupId = null;
        this.exam.sessions[i].slot = null;
      }
    });
    this.sessions = this.sessions.slice(0, index + 1);
    this.clearSlotSelection();
  }

 /**
  * Get Session availabilities
  * */
  getSessionGroups(startDate: any, endDate: any, site?: SiteSlot) {
    //this.selectedExamDetailService.setSessions([]);
    return new Observable((observer: Observer<any>) => {
      this.getSessions(startDate, endDate, site).subscribe((sessionGroups: SessionGroup[]) => {
        this.sessionAvailabilities = [];
        this.exam.sessions.forEach((s, index) => {
          let availability = sessionGroups.map(sg => {
            s.duration = sg.sessions.filter(group => group.sessionId === index + 1)[0].slot.duration;
            return {
              "sessionId": index + 1,
              "groupId": sg.sessions.filter(group => group.sessionId === index + 1)[0].groupId,
              "slot": sg.sessions.filter(group => group.sessionId === index + 1)[0].slot,
              "testDate": sg.sessions.filter(group => group.sessionId === index + 1)[0].slot.testDate,
              "startDateTime": sg.sessions.filter(group => group.sessionId === index + 1)[0].slot.startDateTime,
              "duration": sg.sessions.filter(group => group.sessionId === index + 1)[0].slot.duration
            };
          });

          availability = availability.reduce((a: any, c: any) => {
            const x = a.find((item: any) => item.testDate === c.testDate);
            if (!x) {
              return a.concat([c]);
            } else {
              return a;
            }
          }, []);
          this.sessionAvailabilities.push(availability);
        });
        if (!!this.sessionAvailabilities && this.sessionAvailabilities.every(a => a.length) && !!this.selectedSite && this.selectedSite.Testdate) {
          this.setFirstSession(this.selectedSite, this.selectedSite.Testdate, this.overLapStartDate, this.overLapEndDate, 0);
        }
        this.selectedExamDetailService.setSessionAvailabilities(this.sessionAvailabilities);
        observer.next(null);
        observer.complete();
      });
    });
  }

  getSessions(startDate: any, endDate: any, site: SiteSlot) {
    this.sessionGroups = [];
    let eligibilityId = this.eligibilityDetails ? this.eligibilityDetails[0] && this.eligibilityDetails[0].id : '-1';
    const selectedSiteId = site ? site.id : this.selectedSite.id
    startDate = moment(startDate).format('YYYY-MM-DDTHH:mm:ss');
    endDate = moment(endDate).format('YYYY-MM-DDTHH:mm:ss');
    return new Observable((observer: Observer<any>) => {
      this.schedulingApiService.getSessions(
        selectedSiteId,
        startDate,
        endDate,
        this.examWithTA, eligibilityId, this.selectedExamDetailService.timeFormat).subscribe(sessions => {
          sessions.forEach((session: Session) => {
            let index = this.sessionGroups.findIndex(sg => sg.groupId === session.groupId);
            if (index < 0) {
              const groupSessions = sessions.filter(s => s.groupId === session.groupId);
              this.sessionGroups.push(new SessionGroup(session.groupId, groupSessions));
          }
          });
          this.selectedExamDetailService.setSessionGroups(this.sessionGroups);
          observer.next(this.sessionGroups);
          observer.complete();
        });
    });
  }

  clearSlotSelection() {
    this.selectedSlots = [];
    if (this.holds && this.holds.length) {
      this.holds = [];
      this.selectedExamDetailService.setHolds([]);
    }
    this.validateNavigation();
    this.timerService.clearClock();
  }

  clearSessions() {
    this.sessions = [];
    let session: Session = new Session();
    session.startDate = this.overLapStartDate;
    session.endDate = this.overLapEndDate;
    session.groupId = 1;
    session.sessionId = 1;
    // first session is never selected in flow
    session.selected = false;
    session.hold = this.exam.sessions[0].hold;
    session.slot = this.exam.sessions[0].slot;
    session.confirmationNumber = this.exam.sessions[0].confirmationNumber;
    this.sessions.push(session);
  }

  clearHolds() {
    if (this.holds && this.holds.length) {
      const releaseHoldTasks$: Observable<any>[] = [];

      this.holds.forEach((hold: any) => {
        // Exam Set Call to check b2b for each exams for eligibility
        releaseHoldTasks$.push(this.schedulingApiService.releaseHold(hold));
      });

      forkJoin(releaseHoldTasks$).subscribe(resp => {
        this.holds = [];
        this.selectedExamDetailService.setHolds([]);
      });
    }
  }

  distinct(value: any, index: number, self: any) {
    return self.indexOf(value) === index;
  }


  /**
   * Helper method to add duration
   * @param date
   * @param duration
   */
  getEndDateTime(date: string, duration: any) {
    //duration = this.getDuration(duration);
    return moment(date)
      //.add(duration.hours, 'hours')
      .add(duration, 'minutes')
      .format('YYYY-MM-DDTHH:mm:ss');
  }

  getValidEndDateStart(beginDate: any) {
    if (!beginDate) return;
    return this.utilityService.getRangeEndDateStart(beginDate);
  }

  getValidEndDateEnd(beginDate: any) {
    if (!beginDate) return;
    return this.utilityService.getRangeEndDateEnd(beginDate, this.selectedExamDetailService.maxDays, this.overLapEndDate);
  }

  /**
 * Helper method to gtet common elements from Arrays
 * @param arrays
 */
  getCommonElements(arrays: number[][]) {
    let currentValues = {};
    let commonValues = {};
    for (let i = arrays[0].length - 1; i >= 0; i--) {
      currentValues[arrays[0][i]] = 1;
    }
    for (let i = arrays.length - 1; i > 0; i--) {
      let currentArray = arrays[i];
      for (let j = currentArray.length - 1; j >= 0; j--) {
        if (currentArray[j] in currentValues) {
          commonValues[currentArray[j]] = 1;
        }
      }
      currentValues = commonValues;
      commonValues = {};
    }
    return Object.keys(currentValues).map(function (value) {
      return parseInt(value);
    });
  }

  /**
   * Helper method to get distinct values from Arrays
   * @param array
   */
  getDistinctValues(array: any[]) {
    return array.reduce((a: any, c: any) => {
      const x = a.find((item: any) => item.date === c.date);
      if (!x) {
        return a.concat([c]);
      } else {
        return a;
      }
    }, []);
  }

  getFocus(event: any): void {
    if (event) {
      setTimeout(function () {
        event.popover.elementRef.nativeElement.nextElementSibling.focus();
      }, 100);
    }
  }

  activeClass(avlDate: any, i: number, classString: string): string {
    return this.isDateSelected(avlDate, i) ? classString : '';
  }

  isDateSelected(avlDate: any, i: number): boolean {
    return !!this.sessions[i] && this.sessions[i].selectedDate === avlDate.date;
  }

 }

